import { useState, useEffect } from "react";
import moment from "moment-timezone";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/ui/combobox";
import {
    useListCustomersTodos,
    useListCustomers,
    AUTOMATION_SOURCE_TYPE,
    useCreateActivity,
    useUpdateActivity,
    CreateCustomerActivityInput,
    UpdateCustomerActivityInput,
    Activity,
    ListCustomerOptions,
    TodoSourceFilter,
} from "@/lib/api/customer";
import { useListMinimalUsers, DbUserWithPackages } from "@/lib/api/users";
import { useToast } from "@/components/ui/use-toast";
import { Edit2, Save, X, Plus } from "lucide-react";
import { CommonDestinationTimezone } from "@/lib/api/document";
import { useStytchMember } from "@stytch/react/b2b";
import { getUserDbId } from "@/lib/models/auth";
import { Loader } from "@/components/ui/loader";
import { Input } from "@/components/ui/input";
import { useQueryParamArray } from "@/hooks/use-query-param-array";
import { TodoSourceFilterButtons } from "../TodoSourceFilter";

function renderPagination(
    currentPage: number,
    totalPages: number,
    setPage: (page: number) => void
) {
    if (totalPages < 1) return null;

    return (
        <div className="flex items-center justify-between mt-4">
            <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.max(currentPage - 1, 1))}
                disabled={currentPage <= 1}
            >
                Previous
            </Button>
            <span className="text-sm text-muted-foreground">
                Page {currentPage} of {totalPages}
            </span>
            <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(Math.min(currentPage + 1, totalPages))}
                disabled={currentPage >= totalPages}
            >
                Next
            </Button>
        </div>
    );
}

// Extended Activity type that includes Customer data from API
type ActivityWithCustomer = Activity & {
    Customer?: { long_name: string };
    last_completed_at?: string;
    last_completed_by_id?: string;
    last_assigned_at?: string;
    last_updated_by_id?: string;
};

interface CustomerTodosSectionProps {
    customerId?: string;
    traderId?: string;
    showCustomerName?: boolean;
    allowCustomerSelection?: boolean;
    customerListFilter?: ListCustomerOptions;
    activity_type?: "risk" | "sales_notes";
    assignableUsersFilter?: (users: DbUserWithPackages[]) => DbUserWithPackages[];
}

export function CustomerTodosSection({
    customerId: propCustomerId,
    traderId,
    showCustomerName,
    allowCustomerSelection,
    customerListFilter,
    activity_type,
    assignableUsersFilter,
}: CustomerTodosSectionProps) {
    const { toast } = useToast();
    const { member } = useStytchMember();
    const currentUserId = member ? getUserDbId(member) : "";
    const [customerId, setCustomerId] = useState(propCustomerId);
    const [isCreating, setIsCreating] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [todosPage, setTodosPage] = useState(1);
    
    useEffect(() => {
        setCustomerId(propCustomerId);
    }, [propCustomerId]);

    const [assignedToFilter, setAssignedToFilter] = useState<string>("");
    const [sourceFilter, setSourceFilter] = useState<TodoSourceFilter>("human");
    const [completedStartDate, setCompletedStartDate] = useState<string>("");
    const [completedEndDate, setCompletedEndDate] = useState<string>("");
    const [updatingStatusIds, setUpdatingStatusIds] = useState<Set<string>>(new Set());
    const [updatingAssignedToIds, setUpdatingAssignedToIds] = useState<Set<string>>(new Set());

    const minimalUsersListResult = useListMinimalUsers();
    const minimalCustomersListResult = useListCustomers({ ...(customerListFilter || { minimal: true, hasRiskProfile: true }) });
    const allMinimalUsersList = minimalUsersListResult?.data?.data || [];
    const minimalUsersList = assignableUsersFilter ? assignableUsersFilter(allMinimalUsersList) : allMinimalUsersList;
    const minimalCustomersList = minimalCustomersListResult?.data?.data || [];

    const inProgressTodosQueryResult = useListCustomersTodos({
        customer_type: customerListFilter?.customer_type,
        entity_type: "TODO",
        customer_id: customerId,
        trader_id: traderId,
        page: todosPage,
        pageSize: 5,
        assigned_to_id: assignedToFilter || undefined,
        source: sourceFilter,
        status: "TODO",
    });

    const completedTodosQueryResult = useListCustomersTodos({
        customer_type: customerListFilter?.customer_type,
        entity_type: "TODO",
        customer_id: customerId,
        trader_id: traderId,
        page: todosPage,
        pageSize: 5,
        start_date: completedStartDate ? moment(completedStartDate).format("YYYY-MM-DD") : undefined,
        end_date: completedEndDate ? moment(completedEndDate).format("YYYY-MM-DD") : undefined,
        source: sourceFilter,
        status: "COMPLETED",
    });

    const createActivity = useCreateActivity();
    const updateActivity = useUpdateActivity();

    // `todosPage` drives both lists here, and changing the filter changes both
    // row counts — so the page has to go back to 1 or one of the two can land
    // past its own last page and render empty.
    const handleSourceFilterChange = (value: TodoSourceFilter) => {
        setTodosPage(1);
        setSourceFilter(value);
    };

    const inProgressTodos: ActivityWithCustomer[] = inProgressTodosQueryResult.data?.data?.data || [];
    const completedTodos: ActivityWithCustomer[] = completedTodosQueryResult.data?.data?.data || [];
    const inProgressPagination = inProgressTodosQueryResult.data?.data?.pagination;
    const completedPagination = completedTodosQueryResult.data?.data?.pagination;
    const isLoading = inProgressTodosQueryResult.isLoading || completedTodosQueryResult.isLoading;

    const handleToggleTodo = async (todo: ActivityWithCustomer) => {
        const newStatus = todo.status === "COMPLETED" ? "TODO" : "COMPLETED";
        
        setUpdatingStatusIds(prev => new Set(prev).add(todo.id));
        
        try {
            const updateData = UpdateCustomerActivityInput.safeParse({
                id: todo.id,
                resource_type: "TODO",
                customer_id: todo.customer_id,
                content: todo.content,
                status: newStatus,
                assigned_to_id: todo.last_assigned_to_id || undefined,
                activity_type: activity_type,
            });

            if (!updateData.success) {
                toast({ title: "Failed to update TODO" });
                return;
            }

            const result = await updateActivity(updateData.data);
            if (!result.success) throw result;
        } catch (error) {
            toast({ title: "Failed to update TODO" });
            console.error("Failed to update todo status:", error);
        } finally {
            setUpdatingStatusIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(todo.id);
                return newSet;
            });
        }
    };

    const handleUpdateAssignment = async (todoId: string, userId: string) => {
        const todo = [...inProgressTodos, ...completedTodos].find(t => t.id === todoId);
        if (!todo) return;

        setUpdatingAssignedToIds(prev => new Set(prev).add(todoId));

        try {
            const updateData = UpdateCustomerActivityInput.safeParse({
                id: todoId,
                resource_type: "TODO",
                customer_id: todo.customer_id,
                content: todo.content,
                status: todo.status || "TODO",
                assigned_to_id: userId || null,
                activity_type: activity_type,
            });

            if (!updateData.success) {
                toast({ 
                    title: "Failed to update assignment",
                    variant: "destructive"
                });
                return;
            }

            const result = await updateActivity(updateData.data);
            if (!result.success) throw result;

            toast({
                title: "Assignment updated",
                description: userId ? "TODO assigned" : "Assignment removed"
            });
        } catch (error) {
            console.error("Failed to update todo assignment:", error);
            toast({
                title: "Failed to update assignment",
                variant: "destructive"
            });
        } finally {
            setUpdatingAssignedToIds(prev => {
                const newSet = new Set(prev);
                newSet.delete(todoId);
                return newSet;
            });
        }
    };

    if (isLoading && todosPage === 1) {
        return (
            <div className="flex items-center justify-center p-4">
                <Loader />
                <span className="ml-2 text-sm text-muted-foreground">Loading todos...</span>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            <div className="space-y-2">
                <div className="flex items-center justify-between">
                    <h3 className="font-semibold">To-Dos</h3>
                    <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsCreating(true)}
                        disabled={isCreating}
                    >
                        <Plus className="h-4 w-4 mr-1" />
                        Add To-Do
                    </Button>
                </div>
                <div className="flex flex-col md:flex-row items-start md:items-center gap-2">
                    <TodoSourceFilterButtons value={sourceFilter} onValueChange={handleSourceFilterChange} />
                    <Combobox
                        id="assignedToFilter"
                        dwimOnChange
                        options={[
                            { value: "", label: "Anyone" },
                            ...minimalUsersList.map((user: DbUserWithPackages) => ({
                                value: user.id,
                                label: user.name
                            }))
                        ]}
                        value={assignedToFilter}
                        onValueChange={setAssignedToFilter}
                        placeholder="Filter by Assigned"
                        className="w-[200px] md:ml-auto"
                    />
                </div>
            </div>

            {isCreating && (
                <TodoForm
                    customerId={customerId}
                    onSave={() => {
                        setIsCreating(false);
                        setTodosPage(1);
                    }}
                    onCancel={() => setIsCreating(false)}
                    currentUserId={currentUserId}
                    users={minimalUsersList}
                    allowCustomerSelection={allowCustomerSelection}
                    customersList={minimalCustomersList}
                    activity_type={activity_type}
                />
            )}

            <div className="space-y-4">
                <div>
                    <h4 className="text-sm font-medium mb-2">In Progress</h4>
                    {inProgressTodos.length === 0 && !isCreating ? (
                        <div className="text-center py-4 text-muted-foreground text-sm">
                            No in-progress todos
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {inProgressTodos.map((todo) => (
                                <TodoCard
                                    key={todo.id}
                                    todo={todo}
                                    isEditing={editingId === todo.id}
                                    onEdit={() => setEditingId(todo.id)}
                                    onCancelEdit={() => setEditingId(null)}
                                    onSave={() => {
                                        setEditingId(null);
                                        setTodosPage(1);
                                    }}
                                    onToggle={() => handleToggleTodo(todo)}
                                    onUpdateAssignment={(userId) => handleUpdateAssignment(todo.id, userId)}
                                    users={minimalUsersList}
                                    showCustomerName={showCustomerName}
                                    isUpdatingStatus={updatingStatusIds.has(todo.id)}
                                    isUpdatingAssignment={updatingAssignedToIds.has(todo.id)}
                                    activity_type={activity_type}
                                />
                            ))}
                        </div>
                    )}
                    {inProgressPagination && renderPagination(
                        inProgressPagination.currentPage,
                        inProgressPagination.totalPages,
                        setTodosPage
                    )}
                </div>

                <div>
                    <div className="flex items-center justify-between mb-2">
                        <h4 className="text-sm font-medium">Completed</h4>
                        <div className="flex flex-col md:flex-row gap-2">
                            <div className="flex flex-col gap-1">
                                <label className="text-xs text-muted-foreground">Start Date</label>
                                <Input
                                    type="date"
                                    value={completedStartDate}
                                    onChange={(e) => {
                                        setTodosPage(1);
                                        setCompletedStartDate(e.target.value);
                                        if (!completedEndDate) {
                                            setCompletedEndDate(e.target.value);
                                        }
                                    }}
                                    className="w-[150px] h-8"
                                />
                            </div>
                            <div className="flex flex-col gap-1">
                                <label className="text-xs text-muted-foreground">End Date</label>
                                <Input
                                    type="date"
                                    min={completedStartDate || undefined}
                                    value={completedEndDate}
                                    onChange={(e) => {
                                        setTodosPage(1);
                                        setCompletedEndDate(e.target.value)
                                    }}
                                    className="w-[150px] h-8"
                                />
                            </div>
                        </div>
                    </div>
                    {completedTodos.length === 0 ? (
                        <div className="text-center py-4 text-muted-foreground text-sm">
                            No completed todos
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {completedTodos.map((todo) => (
                                <TodoCard
                                    key={todo.id}
                                    todo={todo}
                                    isEditing={false}
                                    onEdit={() => {}}
                                    onCancelEdit={() => {}}
                                    onSave={() => {}}
                                    onToggle={() => handleToggleTodo(todo)}
                                    onUpdateAssignment={() => {}}
                                    users={minimalUsersList}
                                    showCustomerName={showCustomerName}
                                    isUpdatingStatus={updatingStatusIds.has(todo.id)}
                                    isUpdatingAssignment={false}
                                    activity_type={activity_type}
                                />
                            ))}
                        </div>
                    )}
                    {completedPagination && renderPagination(
                        completedPagination.currentPage,
                        completedPagination.totalPages,
                        setTodosPage
                    )}
                </div>
            </div>
        </div>
    );
}

interface TodoFormProps {
    customerId?: string;
    todo?: ActivityWithCustomer;
    onSave: () => void;
    onCancel: () => void;
    currentUserId: string;
    users: DbUserWithPackages[];
    allowCustomerSelection?: boolean;
    customersList?: any[];
    onCustomerChange?: (customerId: string) => void;
    activity_type?: "risk" | "sales_notes";
}

function TodoForm({ customerId: propCustomerId, todo, onSave, onCancel, currentUserId, users, allowCustomerSelection, customersList, onCustomerChange, activity_type }: TodoFormProps) {
    const [customerId, setCustomerId] = useState(propCustomerId || todo?.customer_id);
    const [content, setContent] = useState(todo?.content || "");
    // Do not default to the current user as its not always somewho who should be doing the work creating the todo
    const [assignedToId, setAssignedToId] = useState(todo?.last_assigned_to_id || "");
    const [isSaving, setIsSaving] = useState(false);
    const { toast } = useToast();
    const createActivity = useCreateActivity();
    const updateActivity = useUpdateActivity();
    
    const handleCustomerChange = (value: string) => {
        setCustomerId(value);
        if (onCustomerChange) {
            onCustomerChange(value);
        }
    };

    const handleSave = async () => {
        if (!content.trim() || isSaving) return;

        setIsSaving(true);
        try {
            if (todo) {
                const updateData = UpdateCustomerActivityInput.safeParse({
                    id: todo.id,
                    resource_type: "TODO",
                    customer_id: customerId,
                    content,
                    status: todo.status || "TODO",
                    assigned_to_id: assignedToId || undefined,
                    activity_type: activity_type,
                });

                if (!updateData.success) {
                    toast({ title: "Invalid data", description: updateData.error.message });
                    return;
                }

                const result = await updateActivity(updateData.data);
                if (!result.success) throw result;
            } else {
                const createData = CreateCustomerActivityInput.safeParse({
                    resource_type: "TODO",
                    customer_id: customerId,
                    content,
                    assigned_to_id: assignedToId || undefined,
                    activity_type: activity_type,
                });

                if (!createData.success) {
                    toast({ title: "Invalid data", description: createData.error.message });
                    return;
                }

                const result = await createActivity(createData.data);
                if (!result.success) throw result;
            }

            onSave();
        } catch (error) {
            console.error("Failed to save todo:", error);
            toast({ title: "Failed to save todo" });
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <Card>
            <CardContent className="p-4">
                <div className="space-y-3">
                    {allowCustomerSelection && !todo && (
                        <Combobox
                            id="customerFilter"
                            dwimOnChange
                            virtualizeList
                            options={customersList?.map((item: any) => ({ value: item.id, label: item.long_name })) || []}
                            value={customerId || ""}
                            onValueChange={handleCustomerChange}
                            placeholder="Select Customer"
                            className=""
                            popoverClassName='md:min-w-[300px] lg:min-w-[400px] xl:min-w-[500px]'
                        />
                    )}
                    <div className="flex gap-2">
                        <Textarea
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            className="flex-1"
                            rows={4}
                            placeholder="Enter todo description..."
                            autoFocus
                        />
                        <div className="flex flex-col gap-1">
                            <Button
                                size="sm"
                                variant="ghost"
                                disabled={isSaving || !content.trim() || !customerId}
                                onClick={handleSave}
                                className="h-8 w-8 p-0"
                            >
                                {isSaving ? <Loader className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                            </Button>
                            <Button
                                size="sm"
                                variant="ghost"
                                disabled={isSaving}
                                onClick={onCancel}
                                className="h-8 w-8 p-0"
                            >
                                <X className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                    <Combobox
                        id="assignToUser"
                        dwimOnChange
                        options={[
                            { value: "", label: "Unassigned" },
                            ...users.map((user: DbUserWithPackages) => ({
                                value: user.id,
                                label: user.name
                            }))
                        ]}
                        value={assignedToId}
                        onValueChange={setAssignedToId}
                        placeholder="Assign to..."
                        className="w-full"
                    />
                </div>
            </CardContent>
        </Card>
    );
}

interface TodoCardProps {
    todo: ActivityWithCustomer;
    isEditing: boolean;
    onEdit: () => void;
    onCancelEdit: () => void;
    onSave: () => void;
    onToggle: () => void;
    onUpdateAssignment: (userId: string) => void;
    users: DbUserWithPackages[];
    showCustomerName?: boolean;
    isUpdatingStatus: boolean;
    isUpdatingAssignment: boolean;
    activity_type?: "risk" | "sales_notes";
}

function TodoCard({ 
    todo, 
    isEditing, 
    onEdit, 
    onCancelEdit, 
    onSave, 
    onToggle,
    onUpdateAssignment,
    users, 
    showCustomerName,
    isUpdatingStatus,
    isUpdatingAssignment,
    activity_type
}: TodoCardProps) {
    const [customerIds, setCustomerIds] = useQueryParamArray("customer_ids");
    const { member } = useStytchMember();
    const currentUserId = member ? getUserDbId(member) : "";
    const isAutomationGenerated = todo.source_type === AUTOMATION_SOURCE_TYPE;

    if (isEditing && !isAutomationGenerated) {
        return (
            <TodoForm
                customerId={todo.customer_id}
                todo={todo}
                onSave={onSave}
                onCancel={onCancelEdit}
                currentUserId={currentUserId}
                users={users}
                allowCustomerSelection={false}
                activity_type={activity_type}
            />
        );
    }

    const isCompleted = todo.status === "COMPLETED";
    const createdBy = todo.CreatedBy?.name || "Unknown";
    const assignedTo = todo.LastAssignedTo?.name || "Unassigned";

    return (
        <Card className="transition-colors duration-200">
            <CardContent className="flex flex-col p-4">
                <div className="flex flex-col md:flex-row items-start justify-between gap-3 mb-2">
                    <div className="flex flex-col md:flex-row items-center gap-2 flex-1 min-w-0">
                        {todo.source_type === AUTOMATION_SOURCE_TYPE && (
                            <Badge
                                variant="outline"
                                className="bg-yellow-50 text-yellow-700 border-yellow-200 flex-shrink-0"
                                title="Created automatically from a call recording"
                            >
                                AI Assistant
                            </Badge>
                        )}
                        {showCustomerName && todo.Customer?.long_name && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="font-semibold text-sm text-red-600"
                                onClick={() => setCustomerIds([todo.customer_id])}
                            >
                                {todo.Customer.long_name}
                            </Button>
                        )}
                    </div>
                    <span>
                        {(todo.last_completed_by_id && todo.last_assigned_to_id) && (
                            <time className="flex flex-col md:grid">
                                <span className="text-xs text-muted-foreground">Last Assigned To</span>
                                <span className="flex flex-row gap-2 text-xs text-muted-foreground">
                                    <time className="flex-shrink-0" dateTime={moment.tz(todo.last_assigned_at, CommonDestinationTimezone).format("YYYY-MM-DD")}>
                                        {moment.tz(todo.last_assigned_at, CommonDestinationTimezone).format("MMM Do YYYY")}
                                    </time>
                                    <span>
                                        by {todo.LastAssignedTo?.name || "Unknown"}
                                    </span>
                                </span>
                            </time>
                        )}
                    </span>
                </div>
                <div className="flex flex-row">
                    <div className="flex flex-col gap-2 justify-start items-center border-r-2 pr-1">
                        {isUpdatingStatus ? (
                            <Loader />
                        ) : (
                            <Checkbox
                                checked={isCompleted}
                                onCheckedChange={onToggle}
                                aria-label="Mark as complete"
                            />
                        )}
                        {!isCompleted && (
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={onEdit}
                                disabled={isAutomationGenerated}
                                title={isAutomationGenerated ? "Automation-generated To-dos cannot be edited" : "Edit To-do"}
                                aria-label={isAutomationGenerated ? "Automation-generated To-do cannot be edited" : "Edit To-do"}
                                className="h-8 w-8 p-0 m-0 disabled:text-gray-300 disabled:opacity-100"
                            >
                                <Edit2 className="h-4 w-4" />
                            </Button>
                        )}
                    </div>

                    <Textarea
                        className={`text-sm mb-3 border-0 ${isCompleted ? 'disabled:text-black' : ''}`}
                        value={todo.content}
                        rows={6}
                        readOnly={true}
                    />
                </div>
                {/* Footer with group and timestamp */}
                <div className="grid grid-cols-2 gap-3 md:gap-0 items-start md:flex md:flex-row md:items-end md:justify-between text-xs text-gray-500">
                    {(!isCompleted) && (
                        <div className="flex flex-col gap-1">
                            <span>Assigned to</span>
                            <Combobox
                                id={`assignToUser-${todo.id}`}
                                dwimOnChange
                                options={[
                                    { value: "", label: "Unassigned" },
                                    ...users.map((user: DbUserWithPackages) => ({
                                        value: user.id,
                                        label: user.name
                                    }))
                                ]}
                                value={todo.last_assigned_to_id || ""}
                                onValueChange={onUpdateAssignment}
                                placeholder=""
                                className="w-full min-w-[200px]"
                                disabled={isUpdatingAssignment}
                            />
                        </div>
                    )}

                    {todo.last_completed_at && (
                        <time className="flex flex-col md:grid">
                            <span>Completed</span>
                            <span className="flex flex-col md:flex-row gap-2">
                                <time className="flex-shrink-0" dateTime={moment.tz(todo.last_completed_at, CommonDestinationTimezone).format("YYYY-MM-DD")}>
                                    {moment.tz(todo.last_completed_at, CommonDestinationTimezone).format("MMM Do YYYY")}
                                </time>
                                <span>
                                    by {todo.LastCompletedBy?.name || "Unknown"}
                                </span>
                            </span>
                        </time>
                    )}

                    <time className="grid">
                        <span>Created At</span>
                        <time className="flex-shrink-0" dateTime={moment.tz(todo.created_at, CommonDestinationTimezone).format("YYYY-MM-DD")}>
                            {moment.tz(todo.created_at, CommonDestinationTimezone).format("MMM Do YYYY")}
                        </time>
                    </time>

                    <div className="grid">
                        <span>Last Updated</span>
                        <time className="flex-shrink-0" dateTime={moment.tz(todo.updated_at, CommonDestinationTimezone).format("YYYY-MM-DD")}>
                            {moment.tz(todo.updated_at, CommonDestinationTimezone).format("MMM Do YYYY")}
                        </time>
                    </div>

                    <div className="grid">
                        <span>Created By:</span>
                        <span className="text-sm text-muted-foreground">
                            {createdBy}
                        </span>
                    </div>
                    <div className="grid">
                        <span>Last Updated By:</span>
                        <span className="text-sm text-muted-foreground">
                            {todo.LastUpdatedBy?.name || "Unknown"}
                        </span>
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}
